"use client";

import { useState, useEffect, Suspense } from "react";
import { X } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { Accordion } from "@/app/components/shadcn/accordion";
import ProductsFilter from "@/app/components/common/product-detail/products/ProductsFilter";
import ProductsGrid from "@/app/components/common/products/ProductsGrid";
import ProductsGridSkeleton from "@/app/components/elements/skeletons/ProductsGridSkeleton";
import ProductsEmptyState from "@/app/components/common/products/ProductsEmptyState";
import InfiniteScrollTrigger from "@/app/components/common/InfiniteScrollTrigger";
import { Product } from "@/app/lib/features/product/product.types";
import useFilters from "@/app/lib/features/filters/filters.hook";
import FilterHeaderSection from "@/app/components/common/product-detail/products/FilterHeaderSection";
import FilterListSection from "@/app/components/common/product-detail/products/FilterListSection";
import FilterPriceSection from "@/app/components/common/product-detail/products/FilterPriceSection";
import {
  FilterListGroup,
  FilterInputGroup,
} from "@/app/lib/features/filters/filters.types";

const FILTER_PARAM_KEYS = [
  "gender", "category", "category_groups", "category_sub_groups",
  "color", "size", "brand", "season", "seller_badge", "rating",
  "discounted", "new_products", "coupon", "bulk_sale", "is_advantageous",
  "flash_products", "min_price", "max_price", "sort", "special_filters",
];

interface ProductsLayoutProps {
  header: React.ReactNode | ((onOpenFilter: () => void) => React.ReactNode);
  products: Product[];
  hasMore: boolean;
  isLoading: boolean;
  isPaginating?: boolean;
  isSuccess?: boolean;
  onLoadMore: () => void;
  showFilter?: boolean;
  onClearFilters?: () => void;
  disableMinimumFilter?: boolean;
}

const MobileFilterClearButton = ({ onClear }: { onClear?: () => void }) => {
  const searchParams = useSearchParams();
  const [isClearing, setIsClearing] = useState(false);

  const hasActiveFilters = FILTER_PARAM_KEYS.some((key) => searchParams.has(key));

  useEffect(() => {
    if (!hasActiveFilters) setIsClearing(false);
  }, [hasActiveFilters]);

  if (!hasActiveFilters && !isClearing) return null;
  if (!onClear) return null;

  return (
    <button
      onClick={() => { setIsClearing(true); onClear(); }}
      disabled={isClearing}
      className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-500 border border-gray-200 hover:border-red-300 rounded-full px-3 py-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <X size={12} />
      Temizle
    </button>
  );
};

const MobileFilterDrawer = ({ onClose, onClearFilters, disableMinimumFilter }: { onClose: () => void; onClearFilters?: () => void; disableMinimumFilter?: boolean }) => {
  const { headerFilters, generalFilters, isLoading, isIdle } = useFilters();

  // Checked (selected_count > 0) olan tüm gruplar açık başlar.
  const defaultOpenValues = generalFilters
    .filter((f) => f.selected_count > 0)
    .map((f) => f.key);

  // Filtreler geç geldiğinde/slug değişiminde Accordion remount olsun diye key.
  const accordionKey = defaultOpenValues.join(",");

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm">Filtreler</span>
          <div className="flex items-center gap-2">
            <Suspense fallback={null}>
              <MobileFilterClearButton onClear={onClearFilters} />
            </Suspense>
            <button onClick={onClose} className="p-1 rounded-md hover:bg-muted">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {(isLoading || isIdle) &&
          !headerFilters.length &&
          !generalFilters.length ? (
            <div className="space-y-3 animate-pulse">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-10 bg-gray-100 rounded" />
              ))}
            </div>
          ) : (
            <>
              <FilterHeaderSection items={headerFilters} />
              <Accordion
                key={accordionKey}
                type="multiple"
                defaultValue={defaultOpenValues}
              >
                {generalFilters.map((filter) => {
                  if (filter.type === "input") {
                    return (
                      <FilterPriceSection
                        key={filter.key}
                        filter={filter as FilterInputGroup}
                      />
                    );
                  }
                  return (
                    <FilterListSection
                      key={filter.key}
                      filter={filter as FilterListGroup}
                      disableMinimumFilter={disableMinimumFilter}
                    />
                  );
                })}
              </Accordion>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const ProductsLayout = ({
  header,
  products,
  hasMore,
  isLoading,
  isPaginating = false,
  isSuccess = false,
  onLoadMore,
  showFilter = true,
  onClearFilters,
  disableMinimumFilter,
}: ProductsLayoutProps) => {
  const isEmpty = !isLoading && isSuccess && products.length === 0;
  const isFilterLoading = isLoading && !isPaginating && products.length > 0;
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const handleOpenFilter = () => setMobileFilterOpen(true);
  const renderedHeader =
    typeof header === "function" ? header(handleOpenFilter) : header;

  return (
    <>
      {/* Mobile filter drawer */}
      {showFilter && mobileFilterOpen && (
        <MobileFilterDrawer onClose={() => setMobileFilterOpen(false)} onClearFilters={onClearFilters} disableMinimumFilter={disableMinimumFilter} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {showFilter && (
          <div className="hidden lg:block lg:col-span-2">
            <ProductsFilter onClearFilters={onClearFilters} disableMinimumFilter={disableMinimumFilter} />
          </div>
        )}
        <div
          className={`col-span-1 ${showFilter ? "lg:col-span-10" : "lg:col-span-12"} space-y-6`}
        >
          {renderedHeader}

          {(isLoading || !isSuccess) && products.length === 0 && (
            <ProductsGridSkeleton />
          )}

          {products.length > 0 && (
            <div
              style={
                isFilterLoading
                  ? {
                      opacity: 0.5,
                      pointerEvents: "none",
                      transition: "opacity 0.2s ease",
                    }
                  : undefined
              }
            >
              <ProductsGrid products={products} />
              <InfiniteScrollTrigger
                onLoadMore={onLoadMore}
                hasMore={hasMore}
                isLoading={isLoading}
              />
            </div>
          )}

          {isEmpty && <ProductsEmptyState />}
        </div>
      </div>
    </>
  );
};

export default ProductsLayout;
